/**
 * tier 對話框「節點 × 品牌可見性」三態判定 — 純函數（P1 唯讀，無 React/AntD 依賴）。
 *
 * 設計依據：docs/operations/backoffice-redesign-2026-06-27.md §5.3 三態徽章 / §5.4 矩陣。
 * 從 TierFormModal.tsx 抽出，使 UI（BrandVisibilityBadge / cross-brand 警示）與這裡的判定
 * 共用同一份語意，並可獨立做 node:test 單元測試（不需 jsdom / bundler）。
 *
 * 🔴 落地紅線（與 node-brand-matrix/effective.ts 的 isBrandAllowed 同語意）：
 *  - `allowedBrands` 為 **fail-OPEN**：NULL / 空陣列＝對「全部品牌」可見（共享基礎設施），
 *    **不可**當成收窄。只有「非空且不含本品牌」才是對本品牌不可見。
 *  - 品牌為動態目錄驅動（tokenManager 的 `Brand = string`），不硬編品牌清單。
 */

import type { Brand } from '../../utils/tokenManager';

/** 三態品牌可見性：本品牌可見 / 全品牌共享(NULL/空) / 僅其他品牌。 */
export type NodeBrandVisibility = 'allowed' | 'shared' | 'other-only';

/**
 * 品牌可見性 fail-OPEN 判定（§0/§5.3 鐵則）。
 * allowedBrands 為 NULL / 空陣列 = 對「全部品牌」可見（共享基礎設施），**不可**當成收窄。
 * 只有「非空且不含本品牌」才是對本品牌不可見。
 */
export function isBrandAllowed(
  allowedBrands: Brand[] | null | undefined,
  brand: Brand
): boolean {
  if (!allowedBrands || allowedBrands.length === 0) return true; // NULL/空 = 全品牌可見
  return allowedBrands.includes(brand);
}

/**
 * 把 allowedBrands 對應到三態徽章。沿用 isBrandAllowed 語意：
 * - shared：NULL/空（fail-OPEN，全品牌共享）
 * - allowed：非空且含本品牌
 * - other-only：非空且不含本品牌（對本品牌不可見、不可用）
 */
export function brandVisibility(
  allowedBrands: Brand[] | null | undefined,
  brand: Brand
): NodeBrandVisibility {
  if (!allowedBrands || allowedBrands.length === 0) return 'shared'; // fail-OPEN
  return isBrandAllowed(allowedBrands, brand) ? 'allowed' : 'other-only';
}
